import {
  API_KEY_DEFAULTS,
  API_MAX_FILE_BYTES,
  type ApiKeyInput,
  type ApiKeySummary,
  type CreatedApiKey,
  EXTRACTION_DEPTH_LABELS,
  EXTRACTION_DEPTHS,
  type ExtractionDepth,
  formatBytes,
  type ReviewMode,
  type Sensitivity,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Lock } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Field } from '../components/guidance/field';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// Admin → API keys (task 39): keys that let other programs send files into Hoopoe (the ingest API). Each key has a
// sensitivity floor and daily quotas; the files go through the same processing as uploads.

export const Route = createFileRoute('/admin/api-keys')({ component: ApiKeysPage });

const TOUR = {
  id: 'admin-api-keys',
  steps: [
    {
      target: '[data-tour="api-key-form"]',
      title: 'One key per program',
      body: 'Give each program or crawler its own key, so you can see what it sends and revoke it alone.',
      learnMore: 'ingest-api',
    },
    {
      target: '[data-tour="api-key-list"]',
      title: 'Quotas and revoking',
      body: 'Each key shows what it sent today against its quotas. Revoke a key the moment it may have leaked.',
    },
  ],
};

const MB = 1024 * 1024;
const SELECT = 'h-9 w-full rounded-md border border-input bg-surface px-2 text-sm';
const when = (ms: number | null) =>
  ms
    ? new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Never';

interface FormState {
  name: string;
  sensitivity: Sensitivity;
  reviewMode: ReviewMode;
  extractionDepth: ExtractionDepth | '';
  dailyFiles: string;
  dailyMb: string;
  maxFileMb: string;
  expires: string;
}

const blank = (): FormState => ({
  name: '',
  sensitivity: 'standard',
  reviewMode: 'later',
  extractionDepth: '',
  dailyFiles: String(API_KEY_DEFAULTS.dailyFiles),
  dailyMb: String(API_KEY_DEFAULTS.dailyBytes / MB),
  maxFileMb: String(API_KEY_DEFAULTS.maxFileBytes / MB),
  expires: '',
});

const fromKey = (k: ApiKeySummary): FormState => ({
  name: k.name,
  sensitivity: k.sensitivity,
  reviewMode: k.reviewMode,
  extractionDepth: k.extractionDepth ?? '',
  dailyFiles: String(k.dailyFiles),
  dailyMb: String(Math.round(k.dailyBytes / MB)),
  maxFileMb: String(Math.round((k.maxFileBytes / MB) * 10) / 10),
  expires: k.expiresAt ? new Date(k.expiresAt).toISOString().slice(0, 10) : '',
});

function toInput(f: FormState): ApiKeyInput {
  return {
    name: f.name,
    sensitivity: f.sensitivity,
    reviewMode: f.reviewMode,
    extractionDepth: f.extractionDepth || null,
    dailyFiles: Number(f.dailyFiles),
    dailyBytes: Math.round(Number(f.dailyMb) * MB),
    maxFileBytes: Math.round(Number(f.maxFileMb) * MB),
    // End of the chosen day, UTC.
    expiresAt: f.expires ? Date.parse(`${f.expires}T23:59:59Z`) : null,
  };
}

function ApiKeysPage() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: ['admin', 'api-keys'], queryFn: () => api<{ keys: ApiKeySummary[] }>('/admin/api-keys') });
  const [form, setForm] = useState<FormState>(blank);
  const [editing, setEditing] = useState<ApiKeySummary | null>(null);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] });

  const save = useMutation({
    mutationFn: (input: ApiKeyInput): Promise<CreatedApiKey | { key: ApiKeySummary; secret?: undefined }> =>
      editing
        ? withReauth(() => api<{ key: ApiKeySummary }>(`/admin/api-keys/${editing.id}`, { method: 'PATCH', body: input }))
        : withReauth(() => api<CreatedApiKey>('/admin/api-keys', { body: input })),
    onSuccess: r => {
      if (r.secret !== undefined) {
        setCreated(r);
        setCopied(false);
        setMessage(null);
      } else setMessage({ kind: 'ok', text: `Saved “${r.key.name}”. The changes apply to the files it sends from now on.` });
      setEditing(null);
      setForm(blank());
      refresh();
    },
    onError,
  });
  const revoke = useMutation({
    mutationFn: (k: ApiKeySummary) => withReauth(() => api<{ key: ApiKeySummary }>(`/admin/api-keys/${k.id}/revoke`, { body: {} })),
    onSuccess: r => {
      setMessage({ kind: 'ok', text: `Revoked “${r.key.name}”. Its next request will be refused; the files it sent stay.` });
      refresh();
    },
    onError,
  });

  const askRevoke = async (k: ApiKeySummary) => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: `Revoke “${k.name}”?`,
          body: 'The program using this key can no longer send files. This can’t be undone: issue a new key to reconnect it.',
          confirmLabel: 'Revoke key',
          cancelLabel: 'Keep it',
          destructive: true,
        })
      : true;
    if (ok) revoke.mutate(k);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    save.mutate(toInput(form));
  };
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({ ...f, [k]: v }));
  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (q.isError)
    return (
      <>
        <PageHeader title="API keys" />
        <ErrorState error={q.error} title="The API keys couldn’t be loaded" onRetry={() => q.refetch()} learnMore="ingest-api" />
      </>
    );
  const keys = q.data?.keys ?? [];
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  return (
    <div className="space-y-8">
      <PageHeader
        title="API keys"
        description="Keys that let other programs send files into Hoopoe. Their files are processed like uploads, with each key's sensitivity and quotas."
      />
      <Tip id="api-keys-secret" learnMore="ingest-api">
        A key is shown once, when it is created. Store it in the other program's secret settings, never in code or a shared document.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      {created && (
        <section aria-labelledby="new-key-title" className="space-y-3 rounded-lg border bg-surface-raised p-4 text-sm">
          <h2 id="new-key-title" className="font-semibold">
            Copy the key for “<DataText>{created.key.name}</DataText>” now
          </h2>
          <p>It won’t be shown again. If it is lost, revoke this key and create a new one.</p>
          <code data-testid="new-api-key" className="block break-all rounded-md border bg-surface p-2 font-mono text-xs">
            {created.secret}
          </code>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={copy}>
              {copied ? 'Copied' : 'Copy key'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>
              I’ve stored it
            </Button>
            <span role="status" className="sr-only">
              {copied ? 'The key is on the clipboard.' : ''}
            </span>
          </div>
        </section>
      )}

      <section aria-labelledby="form-title" className="space-y-4" data-tour="api-key-form">
        <h2 id="form-title" className="font-semibold">
          {editing ? (
            <>
              Change “<DataText>{editing.name}</DataText>”
            </>
          ) : (
            'New key'
          )}
        </h2>
        <form onSubmit={submit} className="grid gap-4 rounded-lg border bg-surface p-4 md:grid-cols-2" aria-labelledby="form-title">
          <Field
            guidance={{
              key: 'name',
              label: 'Name',
              required: true,
              placeholder: 'e.g. News crawler',
              help: 'The program or person that will use the key. It also names the Library collection its files go into.',
            }}
          >
            {p => <Input {...p} value={form.name} maxLength={80} onChange={e => set('name', e.target.value)} />}
          </Field>
          <Field
            guidance={{
              key: 'sensitivity',
              label: 'Sensitivity',
              help: 'Confidential: every file sent with this key is confidential and only ever read by Cloudflare-native models. Standard: files are standard unless a request marks one confidential.',
              learnMore: 'sensitivity-and-ai-routing',
            }}
          >
            {p => (
              <select {...p} className={SELECT} value={form.sensitivity} onChange={e => set('sensitivity', e.target.value as Sensitivity)}>
                <option value="standard">Standard (a request may mark a file confidential)</option>
                <option value="confidential">Confidential (always)</option>
              </select>
            )}
          </Field>
          <Field
            guidance={{
              key: 'reviewMode',
              label: 'Review',
              help: 'Later: extracted items are committed as unreviewed and listed in the Review Inbox. Now: each file waits for review.',
              learnMore: 'review-inbox',
            }}
          >
            {p => (
              <select {...p} className={SELECT} value={form.reviewMode} onChange={e => set('reviewMode', e.target.value as ReviewMode)}>
                <option value="later">Later (Review Inbox)</option>
                <option value="now">Now (each file waits for review)</option>
              </select>
            )}
          </Field>
          <Field
            guidance={{
              key: 'extractionDepth',
              label: 'Extraction depth',
              help: 'What is extracted from each file. Proposed per file chooses from the file’s type, as the Upload page does. A request can still choose its own.',
              learnMore: 'library-and-collections',
            }}
          >
            {p => (
              <select
                {...p}
                className={SELECT}
                value={form.extractionDepth}
                onChange={e => set('extractionDepth', e.target.value as ExtractionDepth | '')}
              >
                <option value="">Proposed per file</option>
                {EXTRACTION_DEPTHS.map(d => (
                  <option key={d} value={d}>
                    {EXTRACTION_DEPTH_LABELS[d]}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field
            guidance={{
              key: 'dailyFiles',
              label: 'Files per day',
              help: 'How many files the key may send per day (UTC). Duplicates count too.',
            }}
          >
            {p => <Input {...p} type="number" min={1} step={1} value={form.dailyFiles} onChange={e => set('dailyFiles', e.target.value)} />}
          </Field>
          <Field
            guidance={{ key: 'dailyMb', label: 'Data per day (MB)', help: 'The total size of the files the key may send per day (UTC).' }}
          >
            {p => <Input {...p} type="number" min={1} step={1} value={form.dailyMb} onChange={e => set('dailyMb', e.target.value)} />}
          </Field>
          <Field
            guidance={{
              key: 'maxFileMb',
              label: 'Largest file (MB)',
              help: `Up to ${formatBytes(API_MAX_FILE_BYTES)}, the most one request can carry. Bigger files go through the Upload page.`,
            }}
          >
            {p => (
              <Input
                {...p}
                type="number"
                min={0.001}
                max={API_MAX_FILE_BYTES / MB}
                step="any"
                value={form.maxFileMb}
                onChange={e => set('maxFileMb', e.target.value)}
              />
            )}
          </Field>
          <Field
            guidance={{
              key: 'expires',
              label: 'Expires',
              help: 'Optional. The key stops working after this day (UTC). Leave empty for a key that works until revoked.',
            }}
          >
            {p => <Input {...p} type="date" value={form.expires} onChange={e => set('expires', e.target.value)} />}
          </Field>
          <div className="flex flex-wrap gap-2 md:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create key'}
            </Button>
            {editing && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditing(null);
                  setForm(blank());
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </section>

      <section aria-labelledby="keys-title" className="space-y-3" data-tour="api-key-list">
        <h2 id="keys-title" className="font-semibold">
          Keys
        </h2>
        {!q.data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : keys.length === 0 ? (
          <EmptyState icon={KeyRound} title="No API keys yet" learnMore="ingest-api">
            Create a key for each program that should send files into Hoopoe, such as a crawler or a partner’s export job.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="API keys">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Key</th>
                  <th className="p-2 text-start font-medium">Sensitivity</th>
                  <th className="p-2 text-start font-medium">Today</th>
                  <th className="p-2 text-start font-medium">Owner</th>
                  <th className="p-2 text-start font-medium">Last used</th>
                  <th className="p-2 text-start font-medium">Status</th>
                  <th className="p-2 text-start font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className="border-b last:border-0" aria-label={k.name}>
                    <td className="p-2">
                      <span className="font-medium">
                        <DataText>{k.name}</DataText>
                      </span>
                      <span className="block font-mono text-xs text-muted-foreground" dir="ltr">
                        {k.prefix}…
                      </span>
                    </td>
                    <td className="p-2">
                      {k.sensitivity === 'confidential' ? (
                        <span className="inline-flex items-center gap-1">
                          <Lock className="size-3.5" aria-hidden="true" /> Confidential
                        </span>
                      ) : (
                        'Standard'
                      )}
                    </td>
                    <td className="p-2" data-testid="api-key-usage">
                      <DataNumber>
                        {k.today.files.toLocaleString()} of {k.dailyFiles.toLocaleString()} files
                      </DataNumber>
                      <span className="block text-xs text-muted-foreground">
                        <DataNumber>
                          {formatBytes(k.today.bytes)} of {formatBytes(k.dailyBytes)}
                        </DataNumber>
                        {' · '}
                        <DataNumber>{k.totalFiles.toLocaleString()}</DataNumber> in all
                      </span>
                    </td>
                    <td className="p-2">
                      <DataText>{k.owner.name ?? k.owner.email}</DataText>
                      {!k.owner.active && <span className="block text-xs text-warning-foreground">Owner can’t upload: key paused</span>}
                    </td>
                    <td className="p-2">
                      <DataNumber>{when(k.lastUsedAt)}</DataNumber>
                    </td>
                    <td className="p-2">
                      {k.status === 'active' ? 'Active' : k.status === 'revoked' ? 'Revoked' : 'Expired'}
                      {k.status === 'active' && k.expiresAt && (
                        <span className="block text-xs text-muted-foreground">
                          until <DataNumber>{new Date(k.expiresAt).toISOString().slice(0, 10)}</DataNumber>
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      {k.status !== 'revoked' && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Change ${k.name}`}
                            onClick={() => {
                              setMessage(null);
                              setEditing(k);
                              setForm(fromKey(k));
                            }}
                          >
                            Change
                          </Button>
                          <Button size="sm" variant="ghost" aria-label={`Revoke ${k.name}`} onClick={() => askRevoke(k)}>
                            Revoke
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="usage-title" className="space-y-2 text-sm">
        <h2 id="usage-title" className="font-semibold">
          Sending a file
        </h2>
        <p>The program sends the file itself as the request body, with its name in the address:</p>
        <pre className="relative overflow-x-auto rounded-md border bg-surface p-3 font-mono text-xs" dir="ltr">
          {`curl -X POST "${origin}/api/ingest/v1/documents?filename=report.pdf" \\
  -H "Authorization: Bearer $HOOPOE_API_KEY" \\
  --data-binary @report.pdf`}
        </pre>
        <LearnMore slug="ingest-api" />
      </section>
    </div>
  );
}
